import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import {
  LivretEval,
  Parcours,
  Teacher,
  TypePositionnement,
} from '../types/livret';
import livretKeys from './keys';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { NotationSystemDTO } from '@teetsh/app/src/containers/Livrets/queries/types/notationSystem';
import { getNotationsHelperAttributes } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/utils/getNotationsHelperAttributes';
import { useTranslation } from 'react-i18next';

interface UpdateLivretEvalDTO {
  id: string;
  name: string;
  notationSystem?: NotationSystemDTO;
  periodeId?: string;
  carnetIds?: string[];
  parcoursList?: Parcours[];
  additionalTeachers?: Teacher[];
  typePositionnement: TypePositionnement;
}

function updateLivretEval({
  schoolyearId,
  schoolId,
  livretEval,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEval: UpdateLivretEvalDTO;
}): Promise<void> {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets/${livretEval.id}`;
  let body: any = {
    id: livretEval.id,
    name: livretEval.name,
    carnetIds: livretEval.carnetIds,
    periodeId: livretEval.periodeId,
    parcoursList: livretEval.parcoursList,
    typePositionnement: livretEval.typePositionnement,
  };
  if (livretEval.additionalTeachers) {
    body = {
      ...body,
      additionalTeachers: livretEval?.additionalTeachers,
    };
  }
  if (livretEval.notationSystem) {
    body = {
      ...body,
      notationSystem: {
        id: livretEval.notationSystem.id,
        name: livretEval.notationSystem.name,
        values: livretEval.notationSystem.values,
      },
    };
  }
  return request(requestURL, {
    method: 'PATCH',
    body,
  });
}

export function useUpdateLivretEval() {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);
  const { mutateAsync } = useMutation({
    mutationFn: (livretEval: UpdateLivretEvalDTO) =>
      updateLivretEval({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEval,
      }),
    onMutate: lEval => {
      queryClient.invalidateQueries({
        queryKey: livretKeys.livretList(schoolyear.id, school.id),
      });
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, lEval.id),
        (oldLivretEval: LivretEval) => {
          let result = { ...oldLivretEval, ...lEval };
          if (lEval.notationSystem) {
            return {
              ...result,
              notationSystem: {
                ...lEval.notationSystem,
                ...getNotationsHelperAttributes(lEval.notationSystem),
              },
            };
          }
          return result as LivretEval;
        },
      );
    },
    onError: () => {
      notifyError({
        title: t('carnet:messages.livret.updateError'),
      });
    },
  });
  return mutateAsync;
}
